import { memo, useCallback, useState } from 'react';
import { useTranslate } from 'react-admin';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import _ from 'lodash';
import ReactJson from 'react-json-view';
import { ClientConfigGroupChip } from './ClientConfigGroupChip';
import type { AppKeyDefinition, ChangedPreviewNode, ClientConfigGroupLike } from '../../lib/clientConfigHelpers';
import {
  CLIENT_CONFIG_INLINE_KEY_RAIL_COLOR,
  findKeyDefinitionByPath,
  getDisplayedSourceGroupIds,
  getClientConfigGroupColor,
  hasActiveGroupSourcesAtOrUnderPath,
  isChangedKeyNewFromAddedGroup,
} from '../../lib/clientConfigHelpers';

export type ClientConfigManagerTargetType = 'customer' | 'group' | 'device';

type SourcesByPath = Record<string, string[]>;

export type ClientConfigChangedPreviewTreeProps = {
  roots: ChangedPreviewNode[];
  sourcesByPath: SourcesByPath;
  overriddenSourcesByPath?: SourcesByPath;
  previousSourcesByPath: SourcesByPath;
  removedGroupIds: Set<string>;
  addedGroupIds: Set<string>;
  removedInlineKeyPathSet: Set<string>;
  configGroupById: Record<string, ClientConfigGroupLike>;
  selectedTargetType: ClientConfigManagerTargetType | null;
  initialKeyDefinitions: AppKeyDefinition[];
  onRestoreInlineKey: (path: string) => void;
  onStageInlineKeyRemoval: (path: string) => void;
};

type NodeProps = Omit<ClientConfigChangedPreviewTreeProps, 'roots'> & {
  node: ChangedPreviewNode;
  depth: number;
  collapsedPaths: Set<string>;
  toggleCollapsed: (path: string) => void;
};

const ChangedPreviewTreeNode = memo(function ChangedPreviewTreeNode({
  node: currentNode,
  depth,
  sourcesByPath,
  overriddenSourcesByPath = {},
  previousSourcesByPath,
  removedGroupIds,
  addedGroupIds,
  removedInlineKeyPathSet,
  configGroupById,
  selectedTargetType,
  initialKeyDefinitions,
  onRestoreInlineKey,
  onStageInlineKeyRemoval,
  collapsedPaths,
  toggleCollapsed,
}: NodeProps) {

  const translate = useTranslate();

  const isCollapsible = currentNode.children.length > 1;
  const isCollapsed = collapsedPaths.has(currentNode.path);

  const currentPathSources = sourcesByPath[currentNode.path] || [];
  const overriddenPathSources = overriddenSourcesByPath[currentNode.path] || [];
  const previousPathSources = previousSourcesByPath[currentNode.path] || [];
  const isLeaf = !currentNode.isObjectNode;
  const displayedSourceGroupIds = getDisplayedSourceGroupIds(
    currentNode.sourceGroupIds,
    currentPathSources,
    removedGroupIds,
    isLeaf,
  );
  const displayedOverriddenGroupIds = overriddenPathSources.filter(
    (groupId) => !removedGroupIds.has(groupId),
  );
  const displayedActiveGroupIds = displayedSourceGroupIds.filter(
    (groupId) => !displayedOverriddenGroupIds.includes(groupId),
  );
  const sourceGroups = displayedActiveGroupIds.map((groupId: string) => configGroupById[groupId]).filter(Boolean);
  const overriddenSourceGroups = displayedOverriddenGroupIds
    .map((groupId: string) => configGroupById[groupId])
    .filter(Boolean);
  const overriddenByInlineKey = isLeaf && overriddenSourceGroups.length > 0;
  const sourceColors = displayedActiveGroupIds.map((groupId: string) => getClientConfigGroupColor(groupId));
  const overriddenSourceColors = displayedOverriddenGroupIds.map((groupId: string) => getClientConfigGroupColor(groupId));
  const newFromAddedGroup = isChangedKeyNewFromAddedGroup(
    currentPathSources,
    previousPathSources,
    addedGroupIds,
  );
  const hadRemovedGroupBefore = previousPathSources.some((groupId) => removedGroupIds.has(groupId));
  const stillEditedByOtherGroup = hadRemovedGroupBefore && currentPathSources.length > 0;
  const removedByGroupChange = hadRemovedGroupBefore && currentPathSources.length === 0;
  const removedByInlineKeyChange = removedInlineKeyPathSet.has(currentNode.path);
  const groupStillProvidesValue = displayedActiveGroupIds.length > 0
    || (removedByInlineKeyChange && hasActiveGroupSourcesAtOrUnderPath(sourcesByPath, currentNode.path));
  const showAsRemovedInlineKey = removedByInlineKeyChange && !groupStillProvidesValue;
  const impactedByUnsavedGroupChange = newFromAddedGroup || hadRemovedGroupBefore;
  const showInlineKeyControls = selectedTargetType === 'device'
    && isLeaf
    && currentNode.sourceGroupIds.length === 0
    && !removedByGroupChange
    && Boolean(findKeyDefinitionByPath(initialKeyDefinitions, currentNode.path));
  const isFromInlineKey = isLeaf && (overriddenByInlineKey || displayedActiveGroupIds.length === 0);
  const railColors = isFromInlineKey
    ? [CLIENT_CONFIG_INLINE_KEY_RAIL_COLOR]
    : (sourceColors.length > 0
      ? sourceColors
      : (overriddenSourceColors.length > 0 ? overriddenSourceColors : [CLIENT_CONFIG_INLINE_KEY_RAIL_COLOR]));
  const borderColor = isFromInlineKey ? 'text.primary' : railColors[railColors.length - 1];
  const isStruckThrough = isLeaf && (removedByGroupChange || showAsRemovedInlineKey);

  const sharedChildProps = {
    sourcesByPath,
    overriddenSourcesByPath,
    previousSourcesByPath,
    removedGroupIds,
    addedGroupIds,
    removedInlineKeyPathSet,
    configGroupById,
    selectedTargetType,
    initialKeyDefinitions,
    onRestoreInlineKey,
    onStageInlineKeyRemoval,
    collapsedPaths,
    toggleCollapsed,
  };

  return (
    <div style={{
      border: '1px solid #ebebeb',
      borderRadius: '4px',
      padding: '8px 10px 8px 14px',
      marginLeft: `${depth * 12}px`,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '8px',
        display: 'flex',
      }}>
        {railColors.map((color, index) => (
          <div key={`${currentNode.path}-rail-${index}`} style={{ flex: 1, backgroundColor: color }}/>
        ))}
      </div>
      <Box>
        <Box sx={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 0.5,
        }}>
          {isCollapsible && (
            <IconButton
              size="small"
              aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
              onClick={() => toggleCollapsed(currentNode.path)}
              sx={{ p: 0.25 }}
            >
              {isCollapsed ? <ChevronRightIcon fontSize="small"/> : <ExpandMoreIcon fontSize="small"/>}
            </IconButton>
          )}
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              textDecoration: isStruckThrough ? 'line-through' : 'none',
            }}
          >
            {currentNode.key}
          </Typography>
          {showInlineKeyControls && (
            <Tooltip title={translate(
              removedByInlineKeyChange
                ? 'clientConfig.manager.restoreInlineKey'
                : 'clientConfig.manager.resetInlineKey',
            )}>
              <IconButton
                size="small"
                aria-label={translate(
                  removedByInlineKeyChange
                    ? 'clientConfig.manager.restoreInlineKey'
                    : 'clientConfig.manager.resetInlineKey',
                )}
                onClick={() => {

                  if (removedByInlineKeyChange) {

                    onRestoreInlineKey(currentNode.path);

                  } else {

                    onStageInlineKeyRemoval(currentNode.path);

                  }

                }}
              >
                {removedByInlineKeyChange ? <UndoIcon fontSize="small"/> : <DeleteIcon fontSize="small"/>}
              </IconButton>
            </Tooltip>
          )}
          {isLeaf && impactedByUnsavedGroupChange && (
            <>
              {!removedByGroupChange && !stillEditedByOtherGroup && (
                <Chip
                  size="small"
                  label={translate(newFromAddedGroup ? 'clientConfig.preview.chip.new' : 'clientConfig.preview.chip.changed')}
                  color={newFromAddedGroup ? 'success' : 'default'}
                  variant={newFromAddedGroup ? 'filled' : 'outlined'}
                />
              )}
              {removedByGroupChange && (
                <Chip
                  size="small"
                  label={translate('clientConfig.preview.chip.removedByGroup')}
                  color="warning"
                  variant="outlined"
                  sx={{
                    textDecoration: 'none',
                    '& .MuiChip-label': { textDecoration: 'none' },
                  }}
                />
              )}
              {stillEditedByOtherGroup && (
                <Chip
                  size="small"
                  label={translate('clientConfig.preview.chip.editedByOtherGroup')}
                  color="info"
                  variant="outlined"
                />
              )}
            </>
          )}
          {isLeaf && showAsRemovedInlineKey && (
            <Chip
              size="small"
              label={translate('clientConfig.preview.chip.removedInlineKey')}
              color="warning"
              variant="outlined"
              sx={{
                textDecoration: 'none',
                '& .MuiChip-label': { textDecoration: 'none' },
              }}
            />
          )}
        </Box>
        {isLeaf && (
          <div style={{ marginBottom: '6px' }}>
            {(_.isPlainObject(currentNode.value) || Array.isArray(currentNode.value))
              ? (
                <Box sx={{ textDecoration: isStruckThrough ? 'line-through' : 'none' }}>
                  <ReactJson
                    src={currentNode.value}
                    name={null}
                    collapsed={1}
                    enableClipboard={false}
                    displayDataTypes={false}
                  />
                </Box>
              )
              : (
                <Typography
                  variant="body2"
                  sx={{
                    color: overriddenByInlineKey || sourceColors.length === 0 ? 'text.primary' : borderColor,
                    fontWeight: overriddenByInlineKey || sourceColors.length > 0 ? 600 : 400,
                    wordBreak: 'break-word',
                    textDecoration: isStruckThrough ? 'line-through' : 'none',
                  }}
                >
                  {String(currentNode.value)}
                </Typography>
              )}
          </div>
        )}
        {(sourceGroups.length > 0 || overriddenSourceGroups.length > 0) ? (
          <div style={{
            display: 'flex',
            gap: '6px',
            flexWrap: 'wrap',
            marginBottom: currentNode.children.length > 0 ? '8px' : 0,
          }}>
            {overriddenSourceGroups.map((group: ClientConfigGroupLike) => (
              <ClientConfigGroupChip
                key={`${currentNode.path}-overridden-${group._id}`}
                group={group}
                struckThrough
              />
            ))}
            {sourceGroups.map((group: ClientConfigGroupLike) => (
              <ClientConfigGroupChip
                key={`${currentNode.path}-${group._id}`}
                group={group}
              />
            ))}
          </div>
        ) : (
          isLeaf && !removedByGroupChange && !showAsRemovedInlineKey && (
            <Typography variant="caption" color="text.secondary">
              {translate('clientConfig.groups.preview.inlineKey')}
            </Typography>
          )
        )}
        {overriddenByInlineKey && !removedByGroupChange && !showAsRemovedInlineKey && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: sourceGroups.length > 0 ? 0.5 : 0 }}>
            {translate('clientConfig.groups.preview.inlineKey')}
          </Typography>
        )}
        {currentNode.children.length > 0 && !isCollapsed && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {currentNode.children.map((child: ChangedPreviewNode) => (
              <ChangedPreviewTreeNode
                key={child.path}
                node={child}
                depth={depth + 1}
                {...sharedChildProps}
              />
            ))}
          </div>
        )}
      </Box>
    </div>
  );

});

export function ClientConfigChangedPreviewTree({
  roots,
  ...sharedProps
}: ClientConfigChangedPreviewTreeProps) {

  const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(() => new Set());

  const toggleCollapsed = useCallback((path: string) => {

    setCollapsedPaths((previous) => {

      const next = new Set(previous);
      if (next.has(path)) {

        next.delete(path);

      } else {

        next.add(path);

      }
      return next;

    });

  }, []);

  return (
    <>
      {roots.map((node) => (
        <ChangedPreviewTreeNode
          key={node.path}
          node={node}
          depth={0}
          collapsedPaths={collapsedPaths}
          toggleCollapsed={toggleCollapsed}
          {...sharedProps}
        />
      ))}
    </>
  );

}
